import React from 'react'
import { Project, ProjectType } from './Projects'
import { motion, AnimatePresence } from 'framer-motion'

interface ProjectListProps {
  filteredProjects: Project[]
}

interface ProjectCardProps {
  id: number
  image: string
  title: string
  location: string
  type: ProjectType
  className?: string
  padded: boolean
}

const ProjectCard: React.FC<ProjectCardProps> = ({
  id,
  image,
  title,
  location,
  type,
  padded,
}) => {
  const marginTop = id % 2 !== 0 && padded ? 'mt-[90px]' : ''

  // Handle potential image loading errors
  const handleImageError = (
    e: React.SyntheticEvent<HTMLImageElement, Event>,
  ) => {
    e.currentTarget.src = `/project-placeholder-${(id % 3) + 1}.jpg`
  }

  return (
    <motion.div
      layout
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className={`rounded-3xl overflow-hidden group transition-all duration-300 ${marginTop}`}
    >
      <div className="relative aspect-[4/3] mb-6">
        <img
          src={image}
          alt={title}
          onError={handleImageError}
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-300" />
      </div>
      <div className="mt-2 transform transition-all duration-300 group-hover:translate-x-2 min-h-[6rem]">
        <span className="inline-block px-3 py-1 rounded-full text-sm bg-gray-100 text-gray-800">
          {type}
        </span>
        <h3 className="mt-2 text-xl font-medium">{title}</h3>
        <p className="text-gray-500">{location}</p>
      </div>
    </motion.div>
  )
}

const ProjectList: React.FC<ProjectListProps> = ({ filteredProjects }) => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
      <AnimatePresence mode="wait">
        {filteredProjects.map((project: Project) => (
          <ProjectCard
            key={project.id}
            padded={filteredProjects.length > 2}
            {...project}
          />
        ))}
      </AnimatePresence>
    </div>
  )
}

export default ProjectList
